import axios from "axios";
import { apiService } from "./apiService.js";
import webAuthService from "./webAuthService.js";

const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:8080",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
};

const apiClient = axios.create(API_CONFIG);

// Primary auth: MSAL token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const accessToken = await apiService.getAccessToken();
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.warn("[API] Failed to add auth token to request:", error.message);
    }
    return config;
  },
  (error) => {
    console.error("[API] Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response: handle auth errors with proper logout
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      console.warn("[API] Authentication error, triggering logout...");
      await apiService.handleAuthError(error, error.response);
    }
    return Promise.reject(error);
  }
);

// Fallback auth: SSO token from localStorage (runs before MSAL interceptor due to LIFO order)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Shared meeting transform to avoid duplication across fetch functions
const transformMeeting = (meeting) => ({
  id: meeting.meeting_id,
  title: meeting.meeting_title || meeting.meeting_id,
  dateTime: new Date(meeting.meeting_start_time * 1000).toISOString(),
  host: "Unknown",
  platform: "Sales Bot",
  joinUrl: `#meeting/${meeting.meeting_id}`,
  status: "Completed",
  meeting_id: meeting.meeting_id,
  meeting_start_time: meeting.meeting_start_time,
  group_id: meeting.group_id,
});

// API functions

/**
 * Fetch meetings from the backend with search and pagination
 * @param {Object} options - Query options
 * @param {string} options.query - Search query text
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.searchMethod - Search method: "embedding", "text", or "both" (default: "both")
 * @returns {Promise<Object>} Object with meetings array and pagination info
 */
export const fetchMeetings = async ({
  query = "",
  page = 1,
  pageSize = 10,
  searchMethod = "both",
} = {}) => {
  try {
    // Get MSAL user ID from web auth
    const userId = await webAuthService.getUserId();

    if (!userId) {
      throw new Error("User not authenticated or user ID not available");
    }

    // Build query parameters
    const params = new URLSearchParams({
      user_id: userId,
      page: page.toString(),
      page_size: pageSize.toString(),
      search_method: searchMethod,
    });

    // Add search query if provided
    if (query && query.trim()) {
      params.append("query", query.trim());
    }

    const response = await apiClient.get(`/meetings?${params.toString()}`);

    if (
      response.data &&
      response.data.status === "success" &&
      response.data.meetings
    ) {
      const transformedMeetings = response.data.meetings.map(transformMeeting);

      return {
        meetings: transformedMeetings,
        pagination: response.data.pagination || {
          current_page: page,
          page_size: pageSize,
          total_items: transformedMeetings.length,
          total_pages: 1,
        },
      };
    }

    return {
      meetings: [],
      pagination: {
        current_page: 1,
        page_size: pageSize,
        total_items: 0,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Failed to fetch meetings:", error);
    throw new Error(`Failed to fetch meetings from backend: ${error.message}`);
  }
};

/**
 * Fetch meetings for a specific group with pagination
 * @param {Object} options - Query options
 * @param {string} options.groupId - Group ID (required)
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.pageSize - Number of items per page (default: 12)
 * @returns {Promise<Object>} Object with meetings array and pagination info
 */
export const fetchGroupMeetings = async ({
  groupId,
  page = 1,
  pageSize = 12,
} = {}) => {
  try {
    if (!groupId) {
      throw new Error("Group ID is required");
    }

    // Get MSAL user ID from web auth
    const userId = await webAuthService.getUserId();

    if (!userId) {
      throw new Error("User not authenticated or user ID not available");
    }

    // Build query parameters
    const params = new URLSearchParams({
      user_id: userId,
      page: page.toString(),
      page_size: pageSize.toString(),
    });

    const response = await apiClient.get(
      `/groups/${groupId}/meetings?${params.toString()}`
    );

    if (response.data && response.data.status === "success") {
      const transformedMeetings = response.data.meetings.map(transformMeeting);

      return {
        meetings: transformedMeetings,
        pagination: response.data.pagination || {
          current_page: page,
          page_size: pageSize,
          total_items: transformedMeetings.length,
          total_pages: 1,
        },
      };
    }

    return {
      meetings: [],
      pagination: {
        current_page: 1,
        page_size: pageSize,
        total_items: 0,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Failed to fetch group meetings:", error);
    throw new Error(
      `Failed to fetch group meetings from backend: ${error.message}`
    );
  }
};

/**
 * Create a new meeting
 * @param {Object} meetingData - Meeting data object
 * @returns {Promise<Object>} Created meeting object
 */
export const createMeeting = async (meetingData) => {
  try {
    const response = await apiClient.post("/meetings", meetingData);
    return response.data;
  } catch (error) {
    console.error("Failed to create meeting:", error);
    throw new Error("Failed to create meeting");
  }
};

/**
 * Update an existing meeting
 * @param {string} meetingId - Meeting ID
 * @param {Object} meetingData - Updated meeting data
 * @returns {Promise<Object>} Updated meeting object
 */
export const updateMeeting = async (meetingId, meetingData) => {
  try {
    const response = await apiClient.put(`/meetings/${meetingId}`, meetingData);
    return response.data;
  } catch (error) {
    console.error("Failed to update meeting:", error);
    throw new Error("Failed to update meeting");
  }
};

/**
 * Delete a meeting
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<void>}
 */
export const deleteMeeting = async (meetingId) => {
  try {
    await apiClient.delete(`/meetings/${meetingId}`);
  } catch (error) {
    console.error("Failed to delete meeting:", error);
    throw new Error("Failed to delete meeting");
  }
};

/**
 * Get meeting by ID
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object>} Meeting object
 */
export const getMeeting = async (meetingId) => {
  try {
    const response = await apiClient.get(`/meetings/${meetingId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to get meeting:", error);
    throw new Error("Failed to get meeting");
  }
};

/**
 * Join a meeting (trigger bot to join)
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object>} Join response
 */
export const joinMeeting = async (meetingId) => {
  try {
    const response = await apiClient.post(`/meetings/${meetingId}/join`);
    return response.data;
  } catch (error) {
    console.error("Failed to join meeting:", error);
    throw new Error("Failed to join meeting");
  }
};

/**
 * Leave a meeting (trigger bot to leave)
 * @param {string} meetingId - Meeting ID
 * @returns {Promise<Object>} Leave response
 */
export const leaveMeeting = async (meetingId) => {
  try {
    const response = await apiClient.post(`/meetings/${meetingId}/leave`);
    return response.data;
  } catch (error) {
    console.error("Failed to leave meeting:", error);
    throw new Error("Failed to leave meeting");
  }
};

/**
 * Get API configuration and status
 * @returns {Promise<Object>} API status and configuration
 */
export const getApiStatus = async () => {
  try {
    const response = await apiClient.get("/status");
    return response.data;
  } catch (error) {
    console.error("Failed to get API status:", error);
    throw new Error("Failed to get API status");
  }
};

/**
 * Update API configuration
 * @param {Object} config - New configuration
 * @returns {Promise<Object>} Updated configuration
 */
export const updateApiConfig = async (config) => {
  try {
    const response = await apiClient.put("/config", config);
    return response.data;
  } catch (error) {
    console.error("Failed to update API config:", error);
    throw new Error("Failed to update API configuration");
  }
};

// Export axios instance for custom requests
export { apiClient };

// Configuration utilities
export const setApiBaseUrl = (baseUrl) => {
  apiClient.defaults.baseURL = baseUrl;
  localStorage.setItem("api_base_url", baseUrl);
};

export const getApiBaseUrl = () => {
  return apiClient.defaults.baseURL;
};

export const setAuthToken = (token) => {
  localStorage.setItem("auth_token", token);
};

export const clearAuthToken = () => {
  localStorage.removeItem("auth_token");
};

// Initialize API configuration from localStorage
const savedBaseUrl = localStorage.getItem("api_base_url");
if (savedBaseUrl) {
  setApiBaseUrl(savedBaseUrl);
}
